<template>
  <div>
    <div v-if="sampleAttribute === 'sampleComment'">
      <VTextarea
        v-model="sample.comment"
        :readonly="isSummaryView"
        hideDetails
        outlined
        :rules="{ max: 50 }"
        dense
        data-cy="cell-sampleComment"
        @input="updateSample($event, 'comment', analysisRequest, sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'labId'">
      <VTextField
        v-model="sample.labId"
        :readonly="isSummaryView"
        data-cy="cell-LabID"
        outlined
        hideDetails
        dense
        @input="updateSample($event, 'labId', analysisRequest, sample.id)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'networkType'">
      {{ analysisRequest.networkType.name }}
    </div>
    <div v-else-if="sampleAttribute === 'componentType'">
      <div v-if="analysisRequest.component">
        {{ analysisRequest.component.type.name }}
      </div>
    </div>
    <div v-else-if="sampleAttribute === 'samples'">
      {{ sample.identification || sample.id }}
    </div>
    <div v-else-if="sampleAttribute === 'receivedDate'">
      <VDateTimePicker
        v-model="sample.receivedDate"
        hideDetails
        :readonly="isSummaryView"
        dense
        :locale="datePickerLocales[$store.getters.lang]"
        @input="updateAnalysisRequest(sampleAttribute, moment($event), analysisRequest, null, true)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'appearance'">
      <VTextField
        v-model="sample.appearance"
        :rules="{required: true}"
        :readonly="isSummaryView"
        hideDetails
        outlined
        dense
        @input="updateAnalysisRequest(sampleAttribute, {value: $event, id: sample.id }, analysisRequest, null, true)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'mineralPhases'">
      <VTextField
        v-model="sample.mineralPhases"
        label="%"
        disabled
        hideDetails
        outlined
        dense
        :readonly="isSummaryView"
      />
    </div>
    <div v-else-if="sampleAttribute === 'organicMaterial'">
      <VTextField
        v-model="sample.organicMaterial"
        label="%"
        hideDetails
        outlined
        :rules="{required: true}"
        :readonly="isSummaryView"
        dense
        @input="updateAnalysisRequest(sampleAttribute, {value: $event, id: sample.id }, analysisRequest, null, true)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'treatment'">
      <VTextarea
        v-model="analysisRequest.treatment"
        :readonly="isSummaryView"
        hideDetails
        dense
        outlined
        @input="updateAnalysisRequest('treatment', $event, analysisRequest, null)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'totalIdentifiedPhases'">
      <VTextField
        v-model="sample.totalIdentifiedPhases"
        label="%"
        disabled
        hideDetails
        outlined
        dense
        :readonly="isSummaryView"
      />
    </div>
    <div v-else-if="sampleAttribute === 'waterType'">
      <VSelect
        v-model="analysisRequest.waterType"
        outlined
        :readonly="isSummaryView"
        :items="waterTypes"
        itemText="name"
        itemValue="key"
        hideDetails
        dense
        @input="updateAnalysisRequest(sampleAttribute, $event, analysisRequest, null)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'materialType'">
      <VSelect
        v-model="analysisRequest.materialType"
        outlined
        :readonly="isSummaryView"
        :items="materialTypes"
        itemText="name"
        itemValue="key"
        hideDetails
        dense
        @input="updateAnalysisRequest(sampleAttribute, $event, analysisRequest, null)"
      />
    </div>
    <div v-else-if="sampleAttribute === 'depositPicture'">
      <SamplePicture
        v-if="isSummaryView"
        :imgId="sample.depositPicture && sample.depositPicture.id"
      />
      <!-- On laisse l'attribut iconLeft vide quand on veut masquer le trombonne -->
      <VFile
        v-else
        v-model="data.depositPicture"
        :isLoading="isLoading"
        :dense="true"
        :hideDetails="true"
        isEdition
        iconLeft=""
        appendOuterIcon="mdi-eye"
        @input="onFileChange(analysisRequest, sample.id, 'depositPicture', $event)"
        @click:clear="onClearClick('depositPicture')"
      />
    </div>
    <div v-else>
      <VTextField
        v-model="analysisRequest[sampleAttribute]"
        :readonly="isSummaryView"
        hideDetails
        dense
        outlined
        @input="updateAnalysisRequest(sampleAttribute, $event, analysisRequest, null)"
      />
    </div>
  </div>
</template>

<script>
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect';
import VDateTimePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDateTimePicker';
import SamplePicture from '@/views/workOrders/summary/SamplePicture';
import { isPlainObject } from 'lodash';
import { mapGetters } from 'vuex';
import FormFilesMixin from '@/mixins/FormFilesMixin';
import VFile from '@/components/shared/widgets/forms/VuetifyInputs/VFile';
import _datePickerLocales from '@/locales/date-picker-locales';
import waterTypes from '@/constants/waterTypes';
import materialTypes from '@/constants/materialTypes';

export default {
    components: {
        VDateTimePicker,
        VFile,
        SamplePicture,
        VTextField,
        VSelect,
        VTextarea,
    },
    mixins: [
        FormFilesMixin,
    ],
    props: {
        analysisRequest: {
            type: Object,
            default: () => ({}),
        },
        analysisRequestId: {
            type: String,
            default: '',
        },
        sampleObj: {
            type: Object,
            default: () => null,
        },
        depositCompositionList: {
            type: Array,
            default: () => ([]),
        },
        sampleAttribute: {
            type: String,
            default: '',
        },
        updateAnalysisRequest: {
            type: Function,
            default: () => {
            },
        },
        isSummaryView: {
            type: Boolean,
            default: false,
        },
        updateSample: {
            type: Function,
            default: () => {
            },
        },
        updateSampleFile: {
            type: Function,
            default: () => {
            },
        },
    },
    data() {
        return {
            mutableDepositCompositions: [],
            isLoading: false,
            data: {},
            fileFields: ['depositPicture'],
            mineralPhases: 0,
            sample: {},
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
        ]),
        datePickerLocales() {
            return _datePickerLocales;
        },
        waterTypes() {
            return waterTypes.map(type => ({
                name: this.$t(`waterTypes.${type}`),
                key: type,
            }));
        },
        materialTypes() {
            return materialTypes.map(type => ({
                name: this.$t(`materialTypes.${type}`),
                key: type,
            }));
        },
    },
    watch: {
        sampleObj: {
            handler() {
                this.sample = { ...this.sampleObj };
            },
            deep: true,
            immediate: true,
        },
    },
    async created() {
        this.data = await this.prepareEditionData(this.sample);
    },
    methods: {
        async onFileChange(analysisRequest, sampleId, field, value) {
            if (value) {
                this.isLoading = true;
                this.updateData(value);
                await this.updateSampleFile(analysisRequest, sampleId, field, value);
                this.isLoading = false;
            }
        },
        onClearClick(field) {
            this.$rq.deleteSamplePicture(this.analysisRequest.id, this.sample.id, field);
        },
        async onClickViewFile(fieldName) {
            const { originalFiles } = this.data;
            if (isPlainObject(originalFiles[fieldName])) {
                const file = originalFiles[fieldName];
                const { data } = await this.$rq.downloadFile(file.id);
                this.$utils.saveFileOnDisk(data, file.fileName);
            }
        },
    },
};
</script>
